import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LogOut, Menu, RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { OPERATIONS_TIME_ZONE_LABEL } from '@/lib/datetime'
import { useAuth } from '@/features/auth/useAuth'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { NAV_ITEMS } from './navigation'
import styles from './Topbar.module.css'

interface TopbarProps {
  onOpenMobileNav: () => void
}

export function Topbar({ onOpenMobileNav }: TopbarProps) {
  const { session, signOut } = useAuth()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [menuOpen, setMenuOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const current =
    NAV_ITEMS.find((item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to))) ??
    NAV_ITEMS[0]

  useEffect(() => {
    if (!menuOpen) return

    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const refresh = async () => {
    setRefreshing(true)
    try {
      // Dispatch decisions are made against live data, so a manual refresh
      // re-reads every active query rather than only the visible one.
      await queryClient.refetchQueries({ type: 'active' })
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <header className={styles.topbar} data-print="hide">
      <button type="button" className={styles.menuButton} onClick={onOpenMobileNav} aria-label="Open navigation">
        <Menu aria-hidden="true" />
      </button>

      <div className={styles.context}>
        <p className={styles.breadcrumb}>Drive For Me · Operations</p>
        <h1 className={styles.pageTitle}>{current.label}</h1>
      </div>

      <div className={styles.tools}>
        <span className={styles.timezone} title="All times are shown in the operational timezone">
          {OPERATIONS_TIME_ZONE_LABEL} time
        </span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => void refresh()}
          loading={refreshing}
          iconLeft={refreshing ? undefined : <RefreshCw aria-hidden="true" />}
          aria-label="Refresh data"
        >
          <span className={styles.refreshLabel}>Refresh</span>
        </Button>

        <div className={styles.account} ref={menuRef}>
          <button
            type="button"
            className={styles.accountButton}
            onClick={() => setMenuOpen((current_) => !current_)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar name={session?.name} src={session?.profileImageUrl} size="sm" />
            <span className={styles.accountText}>
              <span className={styles.accountName}>{session?.name ?? 'Administrator'}</span>
              <span className={styles.accountRole}>Administrator</span>
            </span>
          </button>

          {menuOpen ? (
            <div className={styles.accountMenu} role="menu">
              <div className={styles.accountDetails}>
                <p className={styles.accountDetailName}>{session?.name}</p>
                {session?.email ? <p className={styles.accountDetailMeta}>{session.email}</p> : null}
                {session?.phone ? <p className={styles.accountDetailMeta}>{session.phone}</p> : null}
              </div>
              <button type="button" role="menuitem" className={styles.signOut} onClick={() => signOut('manual')}>
                <LogOut aria-hidden="true" />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  )
}
